import { useMutation, useQueryCache } from "react-query";
import { useToasts } from "react-toast-notifications";
import { ToastStatuses } from "../../../components/global_components/Toast";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import {
  putNotifyNotificationContent,
  PutNotifyNotificationContentBody,
  PutNotifyNotificationContentQuery,
  PutNotifyNotificationContentResponse,
} from "../../../queries/notify";
import { buildNotifyInfiniteNotificationContentQueryKey } from "./useInfiniteNotificationContent";
import { buildNotifyContentDetailsQueryKey } from "./useNotifyContent";

export function useNotifyNotificationContentUpdate({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: (notificationContent: PutNotifyNotificationContentResponse) => void | Promise<void>;
  onError?: () => void;
} = {}) {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const { addToast } = useToasts();

  const [updateNotificationContentMutation, { isLoading: isUpdateNotificationContentLoading }] = useMutation(
    putNotifyNotificationContent(networkHeaders),
    {
      onSuccess: async (notificationContent) => {
        await queryCache.invalidateQueries(buildNotifyInfiniteNotificationContentQueryKey());
        await queryCache.invalidateQueries(
          buildNotifyContentDetailsQueryKey({
            id: notificationContent.id,
          }),
        );
        onSuccess(notificationContent);
      },
      onError: () => {
        addToast("Failed to update notify content.", { appearance: ToastStatuses.Error, autoDismiss: true });
        onError();
      },
    },
  );

  const updateNotificationContent = async (
    id: number,
    body: PutNotifyNotificationContentBody,
    action?: NonNullable<PutNotifyNotificationContentQuery>["action"],
  ) => {
    return await updateNotificationContentMutation({
      id,
      body,
      query: action ? { action } : {},
    });
  };

  return {
    updateNotificationContent,
    isUpdateNotificationContentLoading,
  };
}
